import { PrimitiveType } from 'react-intl';
import I18nTexts from '../common/i18n/I18nTextsCommon';
import { SupportedLanguages } from '../common/i18n/SupportedLanguages';
import { createValidDecimalRegex } from './regexPatterns';
import { getAllNumberFormatRegexByLocal } from './utils';

const REGEX_PRODUCT_NAME = /^[^\s](?:[\p{L}\w\s\-,.+()®™©°/\\]*[^\s])?$/u;
const REGEX_EMAIL = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const REGEX_SHEET_NUMBER = /^[A-Za-z0-9\-/.]{0,20}$/;
const REGEX_URL = /^https:\/\/(www\.)?[a-z0-9-]+(\.[a-z0-9-]+){1,2}\/$/;
const REGEX_PHONENUMBER = /^\+?[0-9-]{0,20}$/;
const REGEX_DEFAULT_TEXT =
    /^[\p{L}0-9\s,\-_.+()®™©°ªº⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ₀₁₂₃₄₅₆₇₈₉₊₋₌₍₎ₐₑₒₓₔₕₖₗₘₙₚₛₜαβγδεζηθικλμνξοπρστυφχψωΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩÅ℃℉;:=!?@#&§$€*'"[\]{}<>|\\/%W~]*$/u;
const REGEX_PERSON_NAME = /^[\p{L}\s-]+$/u;
// Single source of truth for allowed formulation code characters, shared by validation and sanitizing.
const FORMULATION_CODE_ALLOWED_CHARS = '\\p{L}\\w\\-,.%+/()~#:®\\\\\\s';
const REGEX_FORMULATION_CODE = new RegExp(`^[${FORMULATION_CODE_ALLOWED_CHARS}]*$`, 'u');
const REGEX_FORMULATION_CODE_DISALLOWED_CHARS = new RegExp(`[^${FORMULATION_CODE_ALLOWED_CHARS}]`, 'gu');
const REGEX_TAX_ID = /^(?!\s)[\p{L}0-9\s/.-]{1,30}(?<!\s)$/u;

export const validationUtils = {
    isStringEmpty: (value: string | undefined | null) => isStringEmpty(value),
    isArrayEmpty: <T>(value: Array<T> | undefined | null) => isArrayEmpty(value),
    isEmailValid: (value: string | undefined) => isEmail(value),
    isUrlValid: (value: string | undefined) => isValidUrl(value),
    isValidPhonenumber: (value: string | undefined) => isValidPhonenumber(value),
    isValidText: (value: string | undefined) => isValidText(value),
    isValidOrEmptyText: (value: string | undefined) => isStringEmpty(value) || isValidText(value),
    isValidTaxId: (value: string | undefined) => isValidTaxId(value),
    isValidPersonName: (value: string | undefined) => isValidPersonName(value),
    isValidSheetNumber: (value: string | undefined) => isValidSheetNumber(value),
    isValidFormulationCode: (value: string | undefined) => isValidFormulationCode(value),
    isValidProductName: (value: string | undefined) => isValidProductName(value),
    sanitizeFormulationCode: (value: string | undefined) => sanitizeFormulationCode(value),
};

const isStringEmpty = (str: string | undefined | null) => {
    return str === undefined || str === null || str.length === 0;
};

const isArrayEmpty = <T>(arr: Array<T> | undefined | null) => {
    return arr === undefined || arr === null || arr.length === 0;
};

const isEmail = (email?: string) => {
    if (!email) {
        return false;
    }

    // Not strict RFC-5322 compliant, but practical for most use cases
    return REGEX_EMAIL.test(email);
};

const isValidSheetNumber = (sheetNumber?: string) => {
    if (!sheetNumber) {
        return false;
    }

    return REGEX_SHEET_NUMBER.test(sheetNumber);
};

const isValidUrl = (url?: string) => {
    if (!url) {
        return false;
    }

    return REGEX_URL.test(url);
};

const isValidPhonenumber = (phoneNumber?: string) => {
    if (!phoneNumber) {
        return false;
    }

    return REGEX_PHONENUMBER.test(phoneNumber);
};

const isValidText = (text?: string) => {
    if (!text) {
        return false;
    }

    return REGEX_DEFAULT_TEXT.test(text);
};

const isValidPersonName = (text?: string) => {
    if (!text) {
        return false;
    }

    return REGEX_PERSON_NAME.test(text);
};

const isValidProductName = (text?: string) => {
    if (!text) {
        return false;
    }

    return REGEX_PRODUCT_NAME.test(text);
};

const isValidFormulationCode = (text?: string) => {
    if (!text) {
        return false;
    }

    return REGEX_FORMULATION_CODE.test(text);
};

const sanitizeFormulationCode = (text?: string) => {
    if (text === undefined) {
        return undefined;
    }

    return text.replace(REGEX_FORMULATION_CODE_DISALLOWED_CHARS, '');
};

const isValidTaxId = (taxId?: string) => {
    if (!taxId) {
        return false;
    }

    return REGEX_TAX_ID.test(taxId);
};

export const isNumberBiggerOrEqualZero = (amount: string) => {
    const regex = /^[0-9.,]+$/;
    if (!regex.test(amount)) {
        return false;
    }

    const num = parseFloat(amount.replace(',', '.'));
    return !isNaN(num) && num >= 0;
};

export const isNumberBiggerZero = (num: number | undefined) => {
    return num !== undefined && !isNaN(num) && num > 0;
};

export const isNumPositiveOrZero = (value: string | undefined): boolean => {
    return value !== undefined && /^\d+$/.test(value);
};

export const isValidNumberValue = (num: number | undefined) => {
    return isNumberBiggerZero(num) && !isStringEmpty(num?.toString());
};

export const isValidDateValue = (date: string | undefined) => {
    if (isStringEmpty(date) || date === undefined) {
        return false;
    }

    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
};

export const isNumericFieldValid = (langState: SupportedLanguages, name: string | undefined) => {
    if (!name) {
        return false;
    }

    const regexPattern = getAllNumberFormatRegexByLocal(langState);
    const regexResult = regexPattern.test(name.toString());
    return regexResult;
};

export const isNumberOfDecimalsValid = (input: string, numberOFdecimal: number) => {
    const ValidDecimal = createValidDecimalRegex(numberOFdecimal);
    const valid = ValidDecimal.test(input);

    return valid;
};

export const isMaxCharacterLengthValid = (input: string, maxLength: number) => {
    const valid = input.length <= maxLength;
    return valid;
};

export const isNumericValueValid = (
    langState: SupportedLanguages,
    input: string,
    numberOFdecimal: number,
    mandatory: boolean,
    triggered?: boolean,
    allowZero = true
) => {
    const isEmptyInput = isStringEmpty(input);
    //If its not mandatory it can be empty
    if (!mandatory && isEmptyInput) {
        return true;
    }
    const isInvalidNumericField = !isNumericFieldValid(langState, input) && !isEmptyInput;
    const isInvalidDecimals = !isNumberOfDecimalsValid(input, numberOFdecimal ?? 0) && !isEmptyInput;
    const isZeroValue = !allowZero && Number.parseFloat(input) === 0 && !isEmptyInput;
    const inValid = (isEmptyInput && triggered) || isInvalidNumericField || isInvalidDecimals || isZeroValue;

    return !inValid;
};

export const isStringValueValid = (
    input: string,
    maxCharacterLength: number,
    mandatory: boolean,
    triggered?: boolean
) => {
    const isEmptyInput = isStringEmpty(input);
    //If its not mandatory it can be empty
    if (!mandatory && isEmptyInput) {
        return true;
    }

    const isValidInput = isValidText(input);
    const isExceedingMaxLength = !isMaxCharacterLengthValid(input, maxCharacterLength ?? 0) && !isEmptyInput;
    const inValid = ((isEmptyInput || !isValidInput) && triggered) || isExceedingMaxLength;

    return !inValid;
};

export const getErrorMessageForDecimalNumberConfig = (
    text: string,
    allowedNumberOfDecimals: number,
    langState: SupportedLanguages,
    translate: (textKey: keyof I18nTexts, paramsObj?: Record<string, PrimitiveType> | undefined) => string,
    mandatory: boolean,
    allowZero = true
): string => {
    //If its not mandatory it can be empty
    if (!mandatory && isStringEmpty(text)) {
        return '';
    }

    const isInvalidFormat = !isNumericFieldValid(langState, text) && !isStringEmpty(text);
    const isInvalidDecimals = !isNumberOfDecimalsValid(text, allowedNumberOfDecimals) && !isStringEmpty(text);
    const isZeroValue = !allowZero && Number.parseFloat(text) === 0 && !isStringEmpty(text);

    if (isInvalidFormat) {
        return translate('the_input_value_has_invalid_format');
    }

    if (isInvalidDecimals) {
        return translate('the_input_value_has_invalid_Number_Of_Decimals', { number: allowedNumberOfDecimals });
    }

    if (isZeroValue) {
        return translate('value_cannot_be_zero');
    }

    return '';
};

export const getErrorMessageForStringConfig = (
    text: string,
    maxLengthCharacter: number,
    translate: (textKey: keyof I18nTexts, paramsObj?: Record<string, PrimitiveType> | undefined) => string,
    mandatory: boolean
) => {
    //If its not mandatory it can be empty
    if (!mandatory && isStringEmpty(text)) {
        return '';
    }

    if (!isMaxCharacterLengthValid(text, maxLengthCharacter) && !isStringEmpty(text)) {
        return translate('the_input_value_has_invalid_Characters_Length', { number: maxLengthCharacter });
    } else if (!isValidText(text)) {
        return undefined;
    } else {
        return '';
    }
};

export interface PaginatedResponse<T> {
    content?: T[];
}

export const getSafeContentArray = <T>(data: { content?: T[] } | { content?: T[] }[] | undefined | null): T[] => {
    if (Array.isArray(data)) {
        // We're handling this just to be safe — the type allows an array,
        // but in reality this case should never happen.
        return Array.isArray(data[0]?.content) ? data[0].content : [];
    }

    if (!data || !Array.isArray(data.content)) {
        return [];
    }

    return data.content;
};
